/**
 * React Hook for RTDB-cached NFL games with real-time updates
 */

import { useState, useEffect, useCallback } from 'react';
import { rtdbNFLService } from '../rtdbNFLService';
import nflRTDBCache from '../rtdbCache';
import { CACHE_PATHS, logCache } from '../rtdbConfig';

/**
 * Hook for fetching NFL games with RTDB caching and real-time updates
 * @param {Object} params - Query parameters
 * @param {string} params.weekId - Week identifier (e.g., 'week1', 'week2')
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {number} params.season - Season year
 * @param {number} params.teamId - Team ID for filtering
 * @param {boolean} params.live - Fetch only live games
 * @param {boolean} params.enabled - Whether to fetch data
 * @param {boolean} params.realtime - Enable real-time updates
 * @returns {Object} Games data, loading state, error, and refetch function
 */
export const useRTDBGames = ({ 
  weekId,
  date,
  season = 2025,
  teamId,
  live = false,
  enabled = true,
  realtime = true 
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');

  const fetchGames = useCallback(async () => {
    if (!enabled) return;

    try {
      setLoading(true);
      setError(null);
      setCacheStatus('fetching');
      
      logCache('info', 'useRTDBGames fetching', { weekId, date, season, teamId, live });
      
      let response;
      
      if (live) {
        // Fetch live games
        response = await rtdbNFLService.getLiveGames();
      } else if (weekId) {
        // Fetch by week (most common in mobile)
        response = await rtdbNFLService.getGamesForWeek(
          weekId,
          season,
          1, // NFL league ID
          teamId,
          'America/New_York'
        );
      } else if (date) {
        // Fetch by date
        response = await rtdbNFLService.getGames(
          null,
          date,
          1, // NFL league ID
          season,
          teamId,
          null,
          null,
          'America/New_York'
        );
      } else {
        // Default to today's games
        response = await rtdbNFLService.getTodayGames(1, 'America/New_York');
      }
      
      const games = response?.response || [];
      setData(games);
      setCacheStatus('success');
      
      logCache('info', `useRTDBGames loaded ${games.length} games`);
    } catch (err) {
      setError(err.message || 'Failed to load games');
      setCacheStatus('error');
      console.error('Failed to load NFL games:', err);
    } finally {
      setLoading(false);
    }
  }, [weekId, date, season, teamId, live, enabled]);

  // Set up real-time subscription
  useEffect(() => {
    if (!enabled || !realtime) return;

    let unsubscribe = () => {};
    
    // Build cache path based on parameters
    let cachePath;
    
    if (live) {
      cachePath = `${CACHE_PATHS.GAMES_LIVE}/all`;
    } else if (weekId) {
      if (teamId) {
        cachePath = `${CACHE_PATHS.GAMES_WEEKS}/${weekId}_${season}_team_${teamId}`;
      } else {
        cachePath = `${CACHE_PATHS.GAMES_WEEKS}/${weekId}_${season}`;
      }
    } else if (date) {
      if (teamId) {
        cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/team_${teamId}_${season}`;
      } else {
        cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/all_${season}`;
      }
    }

    if (cachePath) {
      // Subscribe to real-time updates
      unsubscribe = nflRTDBCache.subscribe(cachePath, (cachedData) => {
        if (cachedData) {
          logCache('info', 'Real-time update received', { path: cachePath });
          
          const games = cachedData.response || cachedData || [];
          setData(games);
          setCacheStatus('realtime-update');
        } else {
          // Cache expired or missing, trigger refresh
          fetchGames();
        }
      });
    }

    // Initial fetch
    fetchGames();

    return () => {
      unsubscribe();
    };
  }, [weekId, date, season, teamId, live, enabled, realtime]); // fetchGames excluded to avoid loops

  // Computed values for easy access
  const getLiveGames = useCallback(() => {
    return data.filter(game => 
      game.fixture?.status?.short && 
      !['FT', 'AET', 'PEN', 'PST', 'CANC', 'ABD', 'AWD', 'WO'].includes(game.fixture?.status?.short)
    );
  }, [data]);

  const getUpcomingGames = useCallback(() => {
    return data.filter(game => 
      game.fixture?.status?.short === 'NS' || 
      game.fixture?.status?.short === 'TBD'
    );
  }, [data]);

  const getFinishedGames = useCallback(() => {
    return data.filter(game => 
      ['FT', 'AET', 'PEN'].includes(game.fixture?.status?.short)
    );
  }, [data]);

  const getGamesByTeam = useCallback((teamId) => {
    return data.filter(game => 
      game.teams?.home?.id === teamId || 
      game.teams?.away?.id === teamId
    );
  }, [data]);

  const getGamesByStatus = useCallback((status) => {
    return data.filter(game => game.fixture?.status?.short === status);
  }, [data]);

  const sortGamesByTime = useCallback((ascending = true) => {
    const sorted = [...data].sort((a, b) => {
      const timeA = new Date(a.fixture?.date || a.fixture?.timestamp * 1000).getTime();
      const timeB = new Date(b.fixture?.date || b.fixture?.timestamp * 1000).getTime();
      return ascending ? timeA - timeB : timeB - timeA;
    });
    return sorted;
  }, [data]);

  const groupGamesByLeague = useCallback(() => {
    const grouped = {};
    data.forEach(game => {
      const leagueId = game.league?.id;
      const leagueName = game.league?.name || 'Unknown';
      if (!grouped[leagueId]) {
        grouped[leagueId] = {
          id: leagueId,
          name: leagueName,
          games: []
        };
      }
      grouped[leagueId].games.push(game);
    });
    return grouped;
  }, [data]);

  return { 
    data, 
    loading, 
    error, 
    refetch: fetchGames,
    cacheStatus,
    isConnected: nflRTDBCache.isConnected(),
    // Computed helpers
    getLiveGames,
    getUpcomingGames,
    getFinishedGames,
    getGamesByTeam,
    getGamesByStatus,
    sortGamesByTime,
    groupGamesByLeague
  };
};

/**
 * Hook for live NFL games with real-time updates
 */
export const useRTDBLiveGames = ({ enabled = true }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return;

    let unsubscribe = () => {};
    const cachePath = `${CACHE_PATHS.GAMES_LIVE}/all`;

    const fetchLive = async () => {
      setLoading(true);
      try {
        const response = await rtdbNFLService.getLiveGames();
        setData(response?.response || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Subscribe to live updates
    unsubscribe = nflRTDBCache.subscribe(cachePath, (cachedData) => {
      if (cachedData) {
        setData(cachedData.response || cachedData || []);
      } else {
        fetchLive();
      }
    });

    // Initial fetch
    fetchLive();

    // Refresh live data more frequently
    const interval = setInterval(fetchLive, 30000); // Every 30 seconds

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [enabled]);

  return { data, loading, error };
};

/**
 * Hook for games by multiple seasons (used in desktop)
 */
export const useRTDBGamesMultiSeason = ({ 
  date,
  seasons = [2025, 2024, 2023],
  teamId,
  enabled = true 
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState([]);

  const fetchGames = useCallback(async () => {
    if (!enabled || !date) return;

    try {
      setLoading(true);
      setError(null);
      setErrors([]);
      
      const response = await rtdbNFLService.getGamesForSeasons(
        date,
        seasons,
        1, // NFL league ID
        teamId,
        'America/New_York'
      );
      
      setData(response?.response || []);
      setErrors(response?.errors || []);
    } catch (err) {
      setError(err.message || 'Failed to load games');
      console.error('Failed to load multi-season games:', err);
    } finally {
      setLoading(false);
    }
  }, [date, seasons, teamId, enabled]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  return { 
    data, 
    loading, 
    error,
    errors,
    refetch: fetchGames
  };
};